/* eslint-disable unicorn/no-await-expression-member */

import { useCallback, useEffect, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { type IconName } from '@sg/garnish';

import type { UseStripePaymentProps } from '../../types';
import { StripeLogger } from '../../utils';
import { getSelectedPaymentMethod } from './helpers';
import { useLabelAndIconByType, usePaymentMethodChangeHandler } from './hooks';

export const useStripePayment = (props: UseStripePaymentProps) => {
  const {
    isPaymentStatePollingEnabled,
    paymentMethods,
    stripeIntentConfigRef,
  } = props;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripe = useStripe();
  const elements = useElements();

  // ─── State ─────────────────────────────────────────────────────────────────

  const [createdPaymentMethod, setCreatedPaymentMethod] = useState('');
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState(''); // prettier-ignore
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedPaymentIcon, setSelectedPaymentIcon] = useState<IconName>('IconCreditCard'); // prettier-ignore
  const { isApplePay, isGooglePay, isLink, isCashApp } = getSelectedPaymentMethod(selectedPaymentMethod); // prettier-ignore

  // ─── Payment Method Creation ───────────────────────────────────────────────

  const createPaymentMethod = useCallback(
    async (shouldSubmit = false) => {
      if (!stripe || !elements) {
        throw new Error('Stripe not initialized');
      }

      if (shouldSubmit) {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          StripeLogger.logSubmitError(submitError);
          throw new Error(submitError.message);
        }
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        StripeLogger.logCreatePaymentMethodError(error);
        throw new Error(error.message);
      }

      setCreatedPaymentMethod(paymentMethod.id);

      return paymentMethod;
    },
    [elements, stripe],
  );

  // ─── Handle Payment Change ─────────────────────────────────────────────────

  const convertTypeToLabelAndIcon = useLabelAndIconByType();
  const handlePaymentElementChange = usePaymentMethodChangeHandler({
    paymentMethods,
    convertTypeToLabelAndIcon,
    setCreatedPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodType,
    setSelectedPaymentIcon,
    createPaymentMethod,
  });

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) {
      return;
    }

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe, handlePaymentElementChange]);

  // ─── Confirm Callback ──────────────────────────────────────────────────────

  const confirmStripe = useCallback(async () => {
    try {
      if (!stripe || !elements) {
        StripeLogger.logStripeNotInitializedError();
        return { status: 'failed', error: 'Stripe not initialized' };
      }

      const { error: submitError, selectedPaymentMethod } =
        await elements.submit();

      if (submitError) {
        StripeLogger.logSubmitError(submitError);
        return { status: 'failed', error: submitError.message };
      }

      const paymentMethodId = createdPaymentMethod || (await createPaymentMethod())?.id; // prettier-ignore

      if (isPaymentStatePollingEnabled) {
        // Maps the stripe-react-native interface to web.
        return await new Promise((resolve) => {
          stripeIntentConfigRef?.current?.confirmHandler?.(
            {
              id: paymentMethodId,
              paymentMethodType: selectedPaymentMethod,
            } as never,
            false,
            async ({ clientSecret = '' }) => {
              const result = await stripe.confirmPayment({
                elements,
                clientSecret,
                redirect: redirectCondition,
                confirmParams: { return_url: defaultRedirect },
              });

              if (result?.error) {
                resolve({ status: 'failed', error: result.error.message });
                return;
              }

              if (result?.paymentIntent?.status === 'requires_action') {
                resolve({ status: 'canceled' });
                return;
              }

              resolve({ status: 'completed' });
            },
          );
        });
      }

      if (!paymentMethodId) {
        StripeLogger.logNoPaymentMethodCreatedError();
        return { status: 'failed', error: 'No payment method created' };
      }

      return { status: 'completed', paymentMethodId };
    } catch (error) {
      StripeLogger.logGenericError(error as Error);
      return { status: 'failed', error: (error as Error).message };
    }
  }, [
    stripe,
    elements,
    isPaymentStatePollingEnabled,
    createdPaymentMethod,
    createPaymentMethod,
    stripeIntentConfigRef,
  ]);

  return {
    isUpdatingTotal: false,
    isGooglePay,
    isApplePay,
    isLink,
    isCashApp,
    selectedPaymentMethodType,
    selectedPaymentMethod,
    selectedPaymentIcon,
    confirmStripe,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const redirectCondition = 'if_required' as const;
const defaultRedirect = window.location.origin;
